import { findIndex, sortBy, reverse, filter, map, chunk, flatten } from "../utils/lodash";
import { RandomizationModel } from "../models/RandomizationModel";
import { ChoiceModel } from "../models/ChoiceModel";
import { RandomizationPattern } from "../models/enums/RandomizationPattern";
import { QuestionModel } from "../models/QuestionModel";
import { Indexed } from "../models/Indexed";
import { BlockModel } from "../models/BlockModel";
import { getSettingBoolean } from "../models/utilities/settingUtilities";
import { VariableModel } from "../models/VariableModel";
import { AxisModel } from "../models/AxisModel";
import { getQuestionWithName } from "../models/utilities/questionnaireUtilities";
import { translate } from "../models/utilities/translationUtilities";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import { isNullOrEmpty } from "../utils";
import { LogicModel } from "../models/LogicModel";
import { RespondentModel } from "../models/RespondentModel";
import { Randomizer } from "./Randomizer";
import { NavigationResultPageModel } from "../models/NavigationResultPageModel";
import { NavigationResultQuestionModel } from "../models/NavigationResultQuestionModel";
import { QuestionType } from "../models/enums/QuestionType";
import { compareChoices } from "../models/utilities/choiceUtilities";
import { AxisType } from "../models/enums/AxisType";
import { RandomizationBounds } from "../models/RandomizationBounds";

export type LogicEvaluator = (logic: LogicModel) => boolean;

const RandFlags = {
    NONE: 0,
    ROW: 1 << 1,
};

const applyRandomization = (
    respondent: RespondentModel,
    randomization: RandomizationModel,
    elements: Indexed[],
    language: string,
    baseSeed?: number,
    flags?: number,
): Indexed[] => {
    if (baseSeed === undefined) baseSeed = 0;

    // Create the seed from various variables.
    const seed = (randomization.seedAdjust || baseSeed) + (respondent.seed || 0) + (flags || 0);
    const randomizer = new Randomizer(seed);

    // Apply the randomization recursively on the bounds.
    if (randomization.bounds) {
        const { startName, endName } = randomization.bounds;
        if (isNullOrEmpty(startName) || isNullOrEmpty(endName)) {
            return elements;
        }
        const startIndex = findIndex(
            elements,
            (e) => e.name.toUpperCase() === startName.toUpperCase(),
        );
        const endIndex =
            findIndex(elements, (e) => e.name.toUpperCase() === endName.toUpperCase()) + 1;
        const randomizedRange = elements.slice(startIndex, endIndex);
        return [
            ...elements.slice(0, startIndex),
            ...applyRandomization(
                respondent,
                { ...randomization, bounds: undefined },
                randomizedRange,
                language,
                baseSeed,
                flags,
            ),
            ...elements.slice(endIndex),
        ] as Indexed[];
    }

    // Randomize according to different patterns.
    switch (randomization.pattern) {
        case RandomizationPattern.Permutation:
            // Chunk by counts of 3.
            if (randomization.groupCount) {
                const a = chunk(elements, randomization.groupCount);
                const b = sortBy(a, () => randomizer.next());
                return flatten(b) as Indexed[];
            }
            return sortBy(elements, () => randomizer.next());
        case RandomizationPattern.ReverseOrder:
            return reverse(elements);
        case RandomizationPattern.Rotation: {
            // If specified elements per group is less than 1, then make groups of 1
            const groupSize =
                randomization.groupCount && randomization.groupCount > 0
                    ? randomization.groupCount
                    : 1;

            const groups = chunk(elements, groupSize);
            const startingGroupIndex = randomizer.next({
                minValue: 0,
                maxValue: groups.length,
            });

            // Elements are grouped in order, so rotating elements is the same as rotating groups
            // with the first element of the starting group as the starting point
            const startingGroup = groups[startingGroupIndex] ?? [];
            const [startingElement] = startingGroup;
            const startingElementIndex = startingElement?.index ?? 0;
            return sortBy(elements, (element) => element.index < startingElementIndex);
        }
        case RandomizationPattern.SortByCode:
            return sortBy(elements, "value");
        case RandomizationPattern.SortByLabel: {
            const key = (flags & RandFlags.ROW) === RandFlags.ROW ? "LeftLabel" : "TEXT";
            return sortBy(elements, (element) => translate(element as any, key, language));
        }
        case RandomizationPattern.RandomReverse: {
            const rand = randomizer.next({ minValue: 1, maxValue: 10 });
            return rand % 2 === 0 ? reverse(elements) : elements;
        }
        default:
            return elements;
    }
};

export const getRandomization = (
    randomizations: RandomizationModel[],
    index: number,
    bounds?: RandomizationBounds,
) => {
    const r = randomizations[index];
    const b = bounds ?? r.bounds;
    if (r && r.pattern === RandomizationPattern.Synchronized) {
        const synchronizedWithIndex = randomizations.findIndex(
            (rand) => rand.id === r.synchronizedWithId,
        );
        if (synchronizedWithIndex > -1)
            return getRandomization(randomizations, synchronizedWithIndex, b);
    }
    return { ...r, b };
};

export const randomizeQuestions = (
    respondent: RespondentModel,
    block: BlockModel,
    language: string,
): QuestionModel[] => {
    let questions = block.questions || [];
    const filteredQuestions = filter(block.questions, "isPageBreak");
    const pageBreakIndices = map(filteredQuestions, "index");
    // Apply randomizations to questions.
    if (block.randomizations) {
        block.randomizations.forEach((randomization, i) => {
            randomization = getRandomization(block.randomizations, i);
            questions = applyRandomization(
                respondent,
                randomization,
                questions,
                language,
                randomization.id,
            ) as QuestionModel[];
        });
    }
    // Recount question index.
    return questions.map((question, index) => ({
        ...question,
        isPageBreak: pageBreakIndices.includes(index),
        index,
    }));
};

export const getRandomizedBlocks = (
    respondent: RespondentModel,
    questionnaire: QuestionnaireModel,
    language: string,
): BlockModel[] => {
    let blocks = questionnaire.blocks || [];
    // Apply randomizations to blocks.
    if (questionnaire.randomizations) {
        questionnaire.randomizations.forEach((randomization, i) => {
            randomization = getRandomization(questionnaire.randomizations, i);
            blocks = applyRandomization(
                respondent,
                randomization,
                blocks,
                language,
                questionnaire.id,
            ) as BlockModel[];
        });
    }
    // Apply randomized questions to blocks and reassign indices.
    return blocks.map((block, index) => ({
        ...block,
        index,
        questions: randomizeQuestions(respondent, block, language).map((question) => ({
            ...question,
            blockIndex: index,
        })),
    }));
};

export function getRandomizedQuestionIdByName(randomizedBlocks: BlockModel[]) {
    const result: { [name: string]: number[] } = {};
    for (const block of randomizedBlocks) {
        for (const question of block.questions) {
            result[question.name.toUpperCase()] = [question.blockIndex, question.index];
        }
    }
    return result;
}

export function getRandomizedQuestionIdByVariable(randomizedBlocks: BlockModel[]) {
    const result: { [name: string]: number[] } = {};
    for (const block of randomizedBlocks) {
        for (const question of block.questions) {
            for (const variable of question.variableNames) {
                result[variable.toUpperCase()] = [question.blockIndex, question.index];
            }
        }
    }
    return result;
}

const getSynchronizedRandomization = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    randomizationSelector: (el: any) => RandomizationModel,
    variable?: VariableModel,
): RandomizationModel => {
    const randomization =
        (variable ? randomizationSelector(variable) : null) || randomizationSelector(question);
    if (!randomization) return undefined;
    if (randomization.pattern === RandomizationPattern.Synchronized) {
        const synchronizedQuestion = getQuestionWithName(
            questionnaire,
            randomization.synchronizedQuestion,
        );
        if (synchronizedQuestion === undefined) return undefined;
        return getSynchronizedRandomization(
            questionnaire,
            synchronizedQuestion,
            (el) => randomizationSelector(el) || el.choiceListRandomization || el.rowRandomization,
        );
    }
    return { ...randomization, questionId: question.elementId };
};

export const getVariableRandomization = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
) => getSynchronizedRandomization(questionnaire, question, (q) => q.variableRandomization);

export const getListRandomization = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    variable?: VariableModel,
) =>
    getSynchronizedRandomization(
        questionnaire,
        question,
        (q) => q.choiceListRandomization,
        variable,
    );

export const getRowRandomization = (questionnaire: QuestionnaireModel, question: QuestionModel) =>
    getSynchronizedRandomization(questionnaire, question, (q) => q.rowRandomization);

export const getColumnRandomization = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
) => getSynchronizedRandomization(questionnaire, question, (q) => q.columnRandomization);

export const randomizeChoiceList = (
    respondent: RespondentModel,
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    variable: VariableModel,
    language: string,
    serverPage: NavigationResultPageModel,
): ChoiceModel[] => {
    if (serverPage && variable.choiceList) {
        const serverQuestion: NavigationResultQuestionModel = serverPage.questions.find(
            (q: NavigationResultQuestionModel) => q.name === question.name,
        );
        const serverVariable = serverQuestion?.variables?.find((v) => v.name === variable.name);
        if (serverVariable && serverVariable.choices) {
            return sortBy(variable.choiceList, (c1) =>
                findIndex(serverVariable.choices, (c2) => compareChoices(c1, c2, question)),
            );
        }
    }
    let choices = variable.choiceList || [];
    const randomization = getListRandomization(questionnaire, question, variable);
    if (randomization != null) {
        const fixedChoices = variable.choiceList.filter((a) => getSettingBoolean(a, "Fixed"));
        // Exclude fixed choices from randomization
        choices = choices.filter((a) => !getSettingBoolean(a, "Fixed"));
        choices = applyRandomization(
            respondent,
            randomization,
            choices,
            language,
            question.id,
        ) as ChoiceModel[];
        for (const choice of fixedChoices) {
            // Re-add the fixed choices.
            choices.splice(choice.index, 0, choice);
        }
    }
    return choices;
};

const hasOneVariablePerCell = (question: QuestionModel) =>
    [QuestionType.HybridGrid, QuestionType.RunningTotal].includes(question.type);

export const randomizeVariables = (
    respondent: RespondentModel,
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    variables: VariableModel[],
    language: string,
    serverPage: NavigationResultPageModel,
): VariableModel[] => {
    if (serverPage && hasOneVariablePerCell(question)) {
        // No need to randomize one variable
        return variables;
    }
    let randomized: VariableModel[];
    if (serverPage && variables) {
        const serverQuestion: NavigationResultQuestionModel = serverPage.questions.find(
            (q: NavigationResultQuestionModel) => q.name === question.name,
        );
        randomized = sortBy(variables, (v1) =>
            findIndex(
                serverQuestion?.variables,
                (v2) => v2.name?.toUpperCase() === v1.name.toUpperCase(),
            ),
        );
    }
    if (!randomized) {
        randomized = variables || [];
        const randomization = getVariableRandomization(questionnaire, question);
        if (randomization != null)
            randomized = applyRandomization(
                respondent,
                randomization,
                randomized,
                language,
                question.id,
            ) as VariableModel[];
    }
    randomized = randomized.map((variable) => ({
        ...variable,
        choiceList: randomizeChoiceList(
            respondent,
            questionnaire,
            question,
            variable,
            language,
            serverPage,
        ),
    }));
    return randomized;
};

const serverRandomizedAxis = (
    questionName: string,
    serverPage: NavigationResultPageModel,
    elements: AxisModel[],
    type: AxisType,
) => {
    if (serverPage && elements?.length > 0) {
        const question: NavigationResultQuestionModel = serverPage.questions.find(
            (q) => q.name.toUpperCase() === questionName.toUpperCase(),
        );
        if (question) {
            const positions =
                type === AxisType.Row ? question?.rowsPositions : question?.columnsPositions;
            if (positions) {
                return sortBy(elements, (r) => positions.indexOf(r.index));
            }
        }
    }
    return null;
};

export const randomizeColumns = (
    respondent: RespondentModel,
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    language: string,
    serverPage: NavigationResultPageModel,
): AxisModel[] => {
    // Get randomization from API
    let columns = serverRandomizedAxis(
        question.name,
        serverPage,
        question.columns,
        AxisType.Column,
    );
    if (columns === null) {
        // Use client side randomization in case we don't have API information (VMO)
        columns = question.columns || [];
        const randomization = getColumnRandomization(questionnaire, question);
        if (randomization != null)
            columns = applyRandomization(
                respondent,
                randomization,
                columns,
                language,
                question.id,
            ) as AxisModel[];
    }
    // Randomize variables.
    columns = columns.map((column) => ({
        ...column,
        variables: randomizeVariables(
            respondent,
            questionnaire,
            question,
            column.variables,
            language,
            serverPage,
        ),
    }));
    return columns;
};

export const randomizeRows = (
    respondent: RespondentModel,
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    language: string,
    serverPage: NavigationResultPageModel,
): AxisModel[] => {
    // Get randomization from API
    let rows = serverRandomizedAxis(question.name, serverPage, question.rows, AxisType.Row);
    if (rows === null) {
        // Use client side randomization in case we don't have API information (VMO)
        rows = question.rows || [];
        const randomization = getRowRandomization(questionnaire, question);
        if (randomization != null) {
            const fixedRows = rows.filter((r) => getSettingBoolean(r, "Fixed"));
            // Exclude fixed rows from randomization
            rows = rows.filter((a) => !getSettingBoolean(a, "Fixed"));
            rows = applyRandomization(
                respondent,
                randomization,
                rows,
                language,
                question.id,
                RandFlags.ROW,
            ) as AxisModel[];
            for (const row of fixedRows) {
                // Re-add the fixed rows.
                rows.splice(row.index, 0, row);
            }
        }
    }
    // Randomize variables.
    return rows.map((row) => ({
        ...row,
        variables: randomizeVariables(
            respondent,
            questionnaire,
            question,
            row.variables,
            language,
            serverPage,
        ),
    }));
};
